import { isAnalyticsExcludedPath } from '@/components/molecules/shared/mixpanel-guards'

import { analyticsMode } from './analytics-mode'
import type { EcommerceItem, GA4UserData, TrackingUserData } from './tracking.types'

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>
  }
}

/**
 * DataLayerService — pushes events to window.dataLayer for GTM
 *
 * Replaces the old GtmService. Key changes:
 * - No manual SHA256 hashing — GTM auto-hashes via User-Provided Data variable
 * - Uses GA4 user_data format for Enhanced Conversions
 * - Pushes raw PII to dataLayer in `user_data` only (Google Ads Enhanced
 *   Conversions and Meta CAPI hash it); `user_properties`, which GA4 reads,
 *   carries business properties and never PII
 * - Delivers in production and in a staging capture tab (a local array, no
 *   container), nowhere else — see analytics-mode.ts
 */
class DataLayerServiceClass {
  private ga4UserData: GA4UserData | null = null
  private userProperties: Record<string, unknown> = {}
  /** The user whose identity reached the dataLayer in this document, and what it was. */
  private announcedUserId: string | null = null
  private announcedIdentity: string | null = null

  private isExcludedRoute(): boolean {
    if (typeof window === 'undefined') return false
    return isAnalyticsExcludedPath(window.location?.pathname)
  }

  /**
   * The one place an entry reaches `window.dataLayer`. A live document gets its
   * dataLayer from the GTM head snippet; a staging capture document has no
   * snippet, so capture starts the array itself. Returns whether it landed.
   *
   * Fire-and-forget: GTM patches `dataLayer.push` and runs tags inside it, so a
   * throw there is swallowed here — analytics must never break the page or
   * block what the caller does next (the packages step pushes begin_checkout
   * right before the Stripe redirect).
   */
  private deliver(entry: Record<string, unknown>): boolean {
    const mode = analyticsMode()
    if (mode === 'off') return false
    if (this.isExcludedRoute()) return false
    if (typeof window === 'undefined') return false
    if (!window.dataLayer) {
      if (mode !== 'capture') return false
      window.dataLayer = []
    }
    try {
      window.dataLayer.push(entry)
      return true
    } catch {
      return false
    }
  }

  /**
   * Record the user's identity for GTM, once per document (design 2026-09-22
   * §5.2, M5/M12). GTM's data model keeps a pushed key for every later tag, so
   * events no longer carry it: the first report for a user pushes
   * `user_data_ready`; a later change for the same user is written into the
   * model without an event; an unchanged re-report (every layout render, every
   * bootstrap) writes nothing.
   */
  setUser(data: TrackingUserData): void {
    if (!data.user_id) return
    // GA4 Enhanced Conversions format (raw — GTM auto-hashes)
    this.ga4UserData = {
      email_address: data.email,
      phone_number: data.phone,
      address: {
        first_name: data.first_name,
        last_name: data.last_name,
      },
    }

    // Business properties only. GA4 reads `user_properties`, and Google
    // Analytics policy forbids sending it an email, phone number or name, so
    // the raw `em`/`ph`/`fn`/`ln` keys that used to ride here are gone. The
    // raw identity lives in `user_data` above alone, which the Google Ads tag
    // (Enhanced Conversions, through a User-Provided Data variable over
    // `user_data.*`) and Meta CAPI read and hash — push raw, never SHA256 here.
    this.userProperties = {
      user_id: data.user_id,
      plan: data.plan,
      company_type: data.company_type,
      formation_state: data.formation_state,
      company_source: data.company_source,
      onboarding_status: data.onboarding_status,
    }

    const identity = JSON.stringify({ user_data: this.ga4UserData, user_properties: this.userProperties })
    if (this.announcedUserId !== data.user_id) {
      const delivered = this.push('user_data_ready', {
        user_data: this.ga4UserData,
        user_properties: this.userProperties,
      })
      if (delivered) {
        this.announcedUserId = data.user_id
        this.announcedIdentity = identity
      }
      return
    }
    if (identity === this.announcedIdentity) return
    // No `event` key: updates GTM's data model without firing any tag.
    if (this.deliver({ user_data: this.ga4UserData, user_properties: this.userProperties })) {
      this.announcedIdentity = identity
    }
  }

  /** Test seam: forget the stored identity and the announcement. */
  _resetForTests(): void {
    this.ga4UserData = null
    this.userProperties = {}
    this.announcedUserId = null
    this.announcedIdentity = null
  }

  /**
   * Push event to dataLayer
   * Identity is not attached here: it reaches GTM once, through setUser.
   */
  push(eventName: string, params: object = {}): boolean {
    const dataLayerParams: Record<string, unknown> = {
      event: eventName,
      ...(params as Record<string, unknown>),
    }

    // Sanitize value/currency — Meta Pixel rejects non-finite numbers and invalid currency codes.
    // FB CAPI tags forward these fields from ALL events, not just ecommerce ones.
    if ('value' in dataLayerParams) {
      const numValue = Number(dataLayerParams.value)
      dataLayerParams.value = Number.isFinite(numValue) ? numValue : undefined
    }
    if ('currency' in dataLayerParams) {
      const cur = dataLayerParams.currency
      if (typeof cur !== 'string' || cur.length !== 3) {
        dataLayerParams.currency = undefined
      }
    }

    return this.deliver(dataLayerParams)
  }

  /**
   * Push ecommerce event with GA4 ecommerce object
   */
  pushEcommerce(
    eventName: string,
    params: {
      transaction_id?: string
      value: number
      currency: string
      items: EcommerceItem[]
      [key: string]: unknown
    },
  ): boolean {
    const { transaction_id, value, currency, items, ...rest } = params

    // Sanitize value/currency — Meta Pixel rejects non-finite numbers and missing currency
    const numValue = Number(value)
    const safeValue = Number.isFinite(numValue) ? numValue : 0
    const safeCurrency = typeof currency === 'string' && currency.length === 3 ? currency : 'USD'

    return this.push(eventName, {
      ecommerce: {
        transaction_id,
        value: safeValue,
        currency: safeCurrency,
        items,
        tax: 0,
        shipping: 0,
      },
      ...rest,
    })
  }
}

export const dataLayerService = new DataLayerServiceClass()
